import { Box, Button, Checkbox, CloseButton, Drawer, Em, Field, Input, Portal, Select, Text } from '@chakra-ui/react';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ApiError, IndicatorsService } from '@/client/index.ts';
import type {
  IndicatorCategory,
  IndicatorCollectionFrequency,
  IndicatorTrajectory,
  IndicatorYearEntityStatus,
} from '@/client/types.gen.ts';
import useCustomToast from '@/hooks/use-custom-toast.ts';
import { handleError } from '@/utils.ts';

import { collectionFrequency, trajectory } from '../index/IndicatorData.tsx';
import { ManageCollecteUpdateDrawerEntitiesTable } from './ManageCollecteUpdateDrawerEntitiesTable.tsx';

export type PartialIndicatorYearSetting = {
  indicator_id: string;
  category: IndicatorCategory;
  unit: string | null;
  year: number | null;
  consolidated?: boolean | null;
  trajectory?: IndicatorTrajectory | null;
  data_collection_frequency?: IndicatorCollectionFrequency | null;
  composiste_as_simple_indicator?: boolean | null;
  objective?: number | null;
  reference_year?: boolean | null;
};

export const ManageCollecteUpdateDrawer = ({
  year,
  selectedIndicators = [],
  onUpdate,
  open: controlledOpen,
  onOpenChange,
  mode,
}: {
  selectedIndicators: PartialIndicatorYearSetting[];
  onUpdate: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  year: number;
  mode: string | undefined;
}) => {
  const { slug } = useParams({ strict: false });
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const { isMultiple, defaultValues } = useMemo(() => {
    const uniqueValues = (key: keyof PartialIndicatorYearSetting) => {
      const values = selectedIndicators.map((el) => el[key]);
      return new Set(values).size > 1;
    };

    const isMultiple = {
      data_collection_frequency: uniqueValues('data_collection_frequency'),
      trajectory: uniqueValues('trajectory'),
      objective: uniqueValues('objective'),
      consolidated: uniqueValues('consolidated'),
      reference_year: uniqueValues('reference_year'),
      composiste_as_simple_indicator: uniqueValues('composiste_as_simple_indicator'),
    };

    return {
      isMultiple,
      defaultValues: {
        data_collection_frequency: isMultiple.data_collection_frequency
          ? ''
          : selectedIndicators[0]?.data_collection_frequency,
        trajectory: isMultiple.trajectory ? '' : (selectedIndicators[0]?.trajectory ?? ''),
        objective: isMultiple.objective ? '' : (selectedIndicators[0]?.objective ?? ''),
        indicator_ids: selectedIndicators.map((el) => el.indicator_id),
        entities: [] as IndicatorYearEntityStatus[],
        year: year,
        reference_year: isMultiple.reference_year
          ? 'indeterminate'
          : (selectedIndicators[0]?.reference_year ?? undefined),
        composiste_as_simple_indicator: isMultiple.composiste_as_simple_indicator
          ? 'indeterminate'
          : (selectedIndicators[0]?.composiste_as_simple_indicator ?? undefined),
      },
    };
  }, [selectedIndicators, year]);

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      setIsLoading(true);
      const changedValues: Partial<PartialIndicatorYearSetting> = Object.entries(value).reduce<
        Partial<PartialIndicatorYearSetting>
      >((acc, [key, val]) => {
        if (
          key === 'indicator_ids' ||
          JSON.stringify(val) !== JSON.stringify(defaultValues[key as keyof typeof defaultValues])
        ) {
          acc[key as keyof PartialIndicatorYearSetting] = val as any;
        }
        return acc;
      }, {});

      if (Object.keys(changedValues).length === 0) {
        return;
      }
      return mutate({ changedValues, indicator_ids: value.indicator_ids });
    },
  });

  const showToast = useCustomToast();
  const { mutate } = useMutation({
    mutationFn: async ({
      changedValues,
      indicator_ids,
    }: {
      changedValues: Partial<PartialIndicatorYearSetting>;
      indicator_ids: string[];
    }) => {
      return IndicatorsService.updateIndicatorYearSettings({
        year: defaultValues.year,
        companySlug: slug,
        requestBody: { ...changedValues, indicator_ids },
      });
    },
    onSuccess: () => {
      showToast('Success!', 'Collecte updated successfully.', 'success');
      setOpen(false);
      setIsLoading(false);
      onUpdate();
    },
    onError: (error) => {
      showToast('Error!', 'Collecte created error.', 'error');
      setIsLoading(false);
      handleError(error as ApiError, showToast);
    },
  });

  useEffect(() => {
    const firstIndicator = selectedIndicators[0];

    if (!firstIndicator) return;

    const updatedFields = {
      data_collection_frequency:
        isMultiple.data_collection_frequency || !firstIndicator.data_collection_frequency
          ? ''
          : firstIndicator.data_collection_frequency,
      trajectory: isMultiple.trajectory || !firstIndicator.trajectory ? '' : firstIndicator.trajectory,
      objective: isMultiple.objective || !firstIndicator.objective ? '' : String(firstIndicator.objective),
      reference_year: isMultiple.reference_year ? 'indeterminate' : (firstIndicator.reference_year ?? undefined),
      composiste_as_simple_indicator: isMultiple.composiste_as_simple_indicator
        ? 'indeterminate'
        : (firstIndicator.composiste_as_simple_indicator ?? undefined),
    };

    for (const [key, value] of Object.entries(updatedFields)) {
      if (JSON.stringify(form.getFieldValue(key as keyof typeof defaultValues)) !== JSON.stringify(value)) {
        form.setFieldValue(key as keyof typeof defaultValues, value);
      }
    }

    form.setFieldValue(
      'indicator_ids',
      selectedIndicators.map((el) => el.indicator_id)
    );
  }, [selectedIndicators, isMultiple]);

  const displayCompositeAsSimpleIndicator = useMemo(() => {
    return selectedIndicators.some((el) => el.category === 'composite');
  }, [selectedIndicators]);

  return (
    <Drawer.Root size="sm" placement="end" open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Drawer.Trigger asChild>
        <Button size="sm" bg={'primary'} display={controlledOpen != null ? 'none' : 'flex'}>
          {t(mode && mode === 'select' ? 'Ajouter ces indicateurs' : 'collection.updateSelectedIndicators')}
        </Button>
      </Drawer.Trigger>

      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content rounded="md" margin={'{spacing.4}'} h={'calc(100% - {spacing.4} * 2)'}>
            <Drawer.CloseTrigger />
            <Drawer.Header>
              <Drawer.Title>{t('collection.updateSelectedIndicators')}</Drawer.Title>
            </Drawer.Header>
            <Drawer.Body>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit();
                }}
              >
                <form.Field
                  name="data_collection_frequency"
                  validators={{
                    onChange: ({ value }) => (!value ? 'Collection frequency is required' : undefined),
                  }}
                >
                  {({ state, handleChange, handleBlur }) => {
                    return (
                      <>
                        <Select.Root
                          collection={collectionFrequency}
                          defaultValue={state.value && state.value !== '' ? [state.value] : []}
                          onValueChange={(e) => handleChange(e.value.length > 0 ? e.value[0] : '')}
                          onBlur={handleBlur}
                          required
                        >
                          <Select.HiddenSelect />
                          <Select.Label>{t('collection.manage.columnsHeader.selectCollectionFrequency')}</Select.Label>
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText
                                placeholder={
                                  isMultiple.data_collection_frequency
                                    ? '( Multiple value set )'
                                    : 'Select collection frequency'
                                }
                              />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.ClearTrigger />
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Select.Positioner>
                            <Select.Content>
                              {collectionFrequency.items.map((el) => (
                                <Select.Item item={el} key={el.value}>
                                  {el.label}
                                  <Select.ItemIndicator />
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Select.Root>
                        {state.meta.errors.length > 0 ? (
                          <Text color="red" textStyle="xs" ml="3" mt="1">
                            <Em>{state.meta.errors.join(', ')}</Em>
                          </Text>
                        ) : null}
                      </>
                    );
                  }}
                </form.Field>
                <form.Field name="trajectory">
                  {({ state, handleChange, handleBlur }) => {
                    return (
                      <Select.Root
                        mt={4}
                        collection={trajectory}
                        defaultValue={state.value !== '' ? [state.value] : []}
                        onValueChange={(e) => handleChange(e.value.length > 0 ? e.value[0] : '')}
                        onBlur={handleBlur}
                      >
                        <Select.HiddenSelect />
                        <Select.Label>{t('collection.manage.columnsHeader.selectTrajectory')}</Select.Label>
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText
                              placeholder={isMultiple.trajectory ? '( Multiple value set )' : 'Select trajectory'}
                            />
                          </Select.Trigger>
                          <Select.IndicatorGroup>
                            <Select.ClearTrigger />
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                          <Select.Content>
                            {trajectory.items.map((el) => (
                              <Select.Item item={el} key={el.value}>
                                {el.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>
                    );
                  }}
                </form.Field>
                {selectedIndicators.length === 1 && (
                  <form.Field name="objective">
                    {({ state, handleChange, handleBlur }) => {
                      return (
                        <Field.Root mt={4}>
                          <Field.Label>{t('collection.manage.columnsHeader.enterObjective')}</Field.Label>
                          <Input
                            type="number"
                            defaultValue={state.value}
                            placeholder={isMultiple.objective ? '( Multiple value set )' : 'Enter objective'}
                            onBlur={handleBlur}
                            onChange={(e) => handleChange(e.target.value)}
                          />
                        </Field.Root>
                      );
                    }}
                  </form.Field>
                )}
                <form.Field name="reference_year">
                  {({ state, handleChange, handleBlur }) => {
                    return (
                      <Field.Root mt={4} flexDir={'row-reverse'} justifyContent={'flex-end'}>
                        <Field.Label>{t('collection.manage.columnsHeader.referenceYear')}</Field.Label>
                        <Checkbox.Root
                          checked={state.value as boolean | undefined}
                          onBlur={handleBlur}
                          onCheckedChange={(e) => {
                            handleChange(!!e.checked);
                          }}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Field.Root>
                    );
                  }}
                </form.Field>
                {displayCompositeAsSimpleIndicator && (
                  <form.Field name="composiste_as_simple_indicator">
                    {({ state, handleChange, handleBlur }) => {
                      return (
                        <Field.Root mt={4} flexDir={'row-reverse'} justifyContent={'flex-end'}>
                          <Field.Label>{t('collection.manage.columnsHeader.composisteAsSimpleIndicator')}</Field.Label>
                          <Checkbox.Root
                            checked={state.value as boolean | undefined}
                            onBlur={handleBlur}
                            onCheckedChange={(e) => {
                              handleChange(!!e.checked);
                            }}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                          </Checkbox.Root>
                        </Field.Root>
                      );
                    }}
                  </form.Field>
                )}

                <form.Field name="entities">
                  {({ handleChange }) => {
                    return (
                      <Field.Root mt={4}>
                        <ManageCollecteUpdateDrawerEntitiesTable
                          selectedIndicatorYearSettings={selectedIndicators}
                          onChange={(e) => handleChange(e as any)}
                        />
                      </Field.Root>
                    );
                  }}
                </form.Field>
              </form>
            </Drawer.Body>
            <Drawer.Footer>
              <Box justifySelf={'flex-end'} mb={2}>
                <Button variant="outline" onClick={() => setOpen(false)} mr={4}>
                  {t('ui.cancel')}
                </Button>
                <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                  {([canSubmit]) => (
                    <Button
                      type="submit"
                      disabled={!canSubmit}
                      loading={isLoading}
                      loadingText="Saving..."
                      bg={'primary'}
                      onClick={form.handleSubmit}
                    >
                      {t('ui.save')} {isLoading ? '...' : ''}
                    </Button>
                  )}
                </form.Subscribe>
              </Box>
            </Drawer.Footer>
            <Drawer.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default ManageCollecteUpdateDrawer;
